import * as authenticationApi from "@/api/authentication/authentication"
import { AuthenticationRequest, AuthenticationResponse } from "@/api/model"
import nativeHelper from "@/lib/nativeHelper"
import ErrorReporter from "@/services/ErrorReporter"

import DeferredPromise from "./DeferredPromise"
import { useAuthSession } from "./useAuthSession"

interface LogoutOptions {
  onSuccess: () => void
  onError: () => void
}

export const authSession = useAuthSession()
let sessionRefreshPromise: DeferredPromise | null = null

export function useLogin() {
  return authenticationApi.useAuthenticate({
    request: {
      withCredentials: true,
    },
    mutation: {
      onSuccess: (
        response: AuthenticationResponse,
        request: { data: AuthenticationRequest },
      ) => {
        authSession.setSessionData(response, request.data.extendedSession)
      },
    },
  })
}

export async function refreshToken() {
  // on native if we embed an iframe and it refreshed the token, it will
  // invalidate the parent app's refresh token leading to a logout. The iframe
  // will inherit the parent's cookies anyway so it shouldn't matter.
  if (nativeHelper.isIframe() && nativeHelper.isNative()) return

  try {
    const isSessionExtended = authSession.isLongLived()
    const data = await authenticationApi.refreshToken(
      {
        extendedSession: isSessionExtended,
      },
      { withCredentials: true },
    )
    authSession.setSessionData(data, isSessionExtended)

    if (!authSession.isAuthenticated.value) {
      ErrorReporter.report("Received expired token", {
        tokenExpiry: data.accessTokenExpiry,
      })
      authSession.clearSessionData()
    }
  } catch {
    authSession.clearSessionData()
  }
}

export async function ensureFreshTokenIfAuthenticated() {
  if (sessionRefreshPromise) {
    await sessionRefreshPromise.promise
  }
  if (
    !authSession.isAccessTokenValid() &&
    authSession.hasRefreshTokenCookie()
  ) {
    try {
      sessionRefreshPromise = new DeferredPromise()
      await refreshToken()
    } finally {
      sessionRefreshPromise?.resolve()
      sessionRefreshPromise = null
    }
  }
}

export function useLogout(options: LogoutOptions) {
  return authenticationApi.useLogout({
    request: { withCredentials: true },
    mutation: {
      onSuccess: () => {
        authSession.clearSessionData()
        options?.onSuccess()
      },
      onError: (error) => {
        options?.onError()
        ErrorReporter.report(error)
      },
    },
  })
}
